import type { ChangeEvent } from 'react';
import { useFormikContext } from 'formik';

import { makeStyles } from '@mui/styles';

import { FormGroup, Checkbox } from '@mui/material';

import { translate } from '@openclassrooms/translations';
import { useUpdateQuizSessionQuestionsAnswers } from '@openclassrooms/data-connect-web/assessment';
import { Answer } from '@openclassrooms/sdk-web';
import Typography from '@classify/react-core-components/Typography';

import AnswerWrapper from './AnswerWrapper';
import MultipleAnswerWrong from './icons/MultipleAnswerWrong';
import MultipleAnswerRight from './icons/MultipleAnswerRight';

interface Props {
  answers: Answer[];
  questionId: number;
  isCompleted?: boolean;
  quizSessionQuestionId: number;
}

const useStyles = makeStyles(({ spacing }) => ({
  warning: {
    marginBottom: spacing(2),
  },
  answerWrapper: {
    display: 'flex',
    flexDirection: 'row',
  },
  answerStatus: {
    minWidth: 26,
    height: 26,
    alignSelf: 'center',
    paddingRight: spacing(1),
  },
  statusIcon: {
    flexShrink: 0,
    width: 18,
    height: 18,
    margin: 'auto',
  },
  iconWrapper: {
    display: 'flex',
    justifyItems: 'center',
    alignItems: 'center',
    padding: spacing(1),
    width: 24,
    height: 24,
  },
}));

const MultipleAnswerQuestion = ({
  answers,
  questionId,
  isCompleted = false,
  quizSessionQuestionId,
}: Props) => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();
  const [updateAnswer] = useUpdateQuizSessionQuestionsAnswers({
    quizSessionQuestionId,
  });

  const selectedAnswers = values[questionId];

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (isCompleted) {
      return;
    }

    const checkedAnswers = new Set(selectedAnswers);
    const value = Number(e.target.value);

    if (e.target.checked) {
      checkedAnswers.add(value);
    } else {
      checkedAnswers.delete(value);
    }

    setFieldValue(`${questionId}`, Array.from(checkedAnswers));
    updateAnswer([...checkedAnswers]);
  };

  const getControlElement = (isCorrect: boolean, isChecked: boolean, id: string) => {
    if (isCorrect === null || !isChecked) {
      return (
        <Checkbox
          name={String(questionId)}
          checked={isChecked}
          value={String(id)}
          onChange={handleChange}
          inputProps={{
            // @ts-ignore
            'data-testid': 'answer',
          }}
        />
      );
    }

    if (isCorrect && isChecked) {
      return (
        <span className={classes.iconWrapper} aria-checked="true">
          <MultipleAnswerRight className={classes.statusIcon} />
        </span>
      );
    }

    return (
      <span className={classes.iconWrapper} aria-checked="true">
        <MultipleAnswerWrong className={classes.statusIcon} />
      </span>
    );
  };

  return (
    <>
      <Typography variant="caption" className={classes.warning}>
        {translate('mcq.view.message.multi_answer_warning')}
      </Typography>
      <FormGroup>
        {answers.map(({ id, content, isCorrect, selected }) => {
          const isChecked = selectedAnswers.includes(id);

          return (
            <AnswerWrapper
              key={`${questionId}-${id}`}
              content={content}
              isCorrect={Boolean(isCorrect)}
              control={getControlElement(isCorrect, isChecked, id)}
              isCompleted={isCompleted}
              warningLabel={
                isCorrect && !selected
                  ? translate('quiz.correction.question.item.multiple_choice.answer_missing')
                  : null
              }
            />
          );
        })}
      </FormGroup>
    </>
  );
};

export default MultipleAnswerQuestion;
